import React from 'react';
import { NavLink, useParams } from 'react-router-dom';
import ProcessLocked from '../../assets/icons/process_locked.svg';
import ProcessFinished from '../../assets/icons/process_finished.svg';
import ProcessesIcon from './ProcessIcon.component';
import { useStyles } from './ProcessesList.styles';
import {
  getAssessmentSessionProcess,
  getCurrentProcess,
  getProcessLabel,
  isProcessFinished,
} from '../../utils/assessmentSessionProcess';
import {
  assessmentSessionProcessOrder,
  AssessmentSessionProcessTypeId,
} from '../../services/exams/assessmentSession/assessments.types';
import { ExaminerFunction } from '../../pages/Assessment/Assessment.types';
import { getAssessmentUrl } from '../../utils/routerUrls';
import useSessionUser from '../../hooks/useSessionUser';
import { AssessmentSessionProcess } from '../../pages/Dashboard/Dashboard.types';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import TooltipOne from '../TooltipOne/TooltipOne.component';

interface ProcessesListProps {
  isMinimized?: boolean;
}

const ProcessesList: React.FC<ProcessesListProps> = (props) => {
  const classes = useStyles();
  const assessmentSession = useSelector((state: RootState) => state.assessmentSession.assessmentSession);
  const { isMinimized } = props;
  const assessmentSessionProcesses: AssessmentSessionProcess[] = getAssessmentSessionProcess();
  const currentProcess = getCurrentProcess(assessmentSessionProcesses);

  const { assessmentSessionId: assessmentSessionIdFromUrl, examinerId } = useParams() as {
    assessmentSessionId: string;
    examinerId: string;
  };

  let assessmentSessionId = assessmentSessionIdFromUrl;

  if (!assessmentSessionId) {
    if (assessmentSession) {
      assessmentSessionId = assessmentSession.assessmentSessionId;
    }
  }

  const { userAssessmentSessionInfo } = useSessionUser();
  const userFunction = userAssessmentSessionInfo.function;

  const isSpecialUser =
    userFunction !== undefined &&
    [ExaminerFunction.PZE, ExaminerFunction.KK, ExaminerFunction.KGZ].includes(userFunction);

  const getIcon = (process: AssessmentSessionProcess) => {
    if (isSpecialUser || currentProcess === process.assessmentSessionProcessTypeId) {
      return null;
    }

    const isFinished = isProcessFinished(process, currentProcess);

    let icon = null;
    let wrapperStyle = null;

    const isProcessFinishedIcon = isFinished && userFunction === ExaminerFunction.CZE;

    if (!isFinished && process.isCurrentProcess === false) {
      icon = <img src={ProcessLocked} className={classes.lockedIcon} alt="ikona zablokowany proces" />;
      wrapperStyle = classes.lockedWrapperMinimized;
    } else if (isProcessFinishedIcon) {
      icon = <img src={ProcessFinished} className={classes.finishedIcon} alt="ikona skończony proces" />;
      wrapperStyle = classes.finishedWrapperMinimized;
    }

    if (icon && wrapperStyle) {
      return (
        <TooltipOne title={isProcessFinishedIcon ? 'Proces ukończony pomyślnie' : 'Brak dostępu do procesu'} placement="top">
          <span
            className={[classes.iconWrapper]
              .concat(...(isMinimized ? [classes.minimized, wrapperStyle] : []))
              .join(' ')}
          >
            {icon}
          </span>
        </TooltipOne>
      );
    }
  };

  const getLink = (process: AssessmentSessionProcess, url: string, children: React.ReactNode) => {
    const enabledLink =
      (userFunction !== undefined &&
        [ExaminerFunction.PZE, ExaminerFunction.KK, ExaminerFunction.KGZ].includes(userFunction)) ||
      process.isCurrentProcess ||
      isProcessFinished(process, currentProcess);

    return enabledLink ? (
      <NavLink
        to={getAssessmentUrl(assessmentSessionId, process.assessmentSessionProcessTypeId.toString(), examinerId)}
        activeClassName={classes.activeLink}
      >
        {children}
      </NavLink>
    ) : (
      <a>{children}</a>
    );
  };

  if (!assessmentSessionProcesses.length) {
    return null;
  }
  return (
    <>
      <ul className={classes.sidebarMenu}>
        <>
          {assessmentSessionProcessOrder.map((type: AssessmentSessionProcessTypeId) => {
            const process = assessmentSessionProcesses.find(
              (item: AssessmentSessionProcess) => item.assessmentSessionProcessTypeId === type,
            );
            if (
              !process ||
              (userAssessmentSessionInfo.isChairman &&
                process.assessmentSessionProcessTypeId === AssessmentSessionProcessTypeId.Scoring)
            ) {
              return null;
            }
            const processLabel = getProcessLabel(process.assessmentSessionProcessTypeId);

            return (
              <li key={process.assessmentSessionProcessTypeId}>
                {getLink(
                  process,
                  getAssessmentUrl(assessmentSessionId, process.assessmentSessionProcessTypeId.toString(), examinerId),
                  <>
                    <ProcessesIcon letter={processLabel.letter} tooltip={isMinimized ? processLabel.label : ''} />
                    {!isMinimized && <span className={classes.sideBarText}>{processLabel.label}</span>}
                    {getIcon(process)}
                  </>,
                )}
              </li>
            );
          })}
        </>
      </ul>
    </>
  );
};

export default ProcessesList;
